import type { Channel } from "core/models";
import { Selection } from "core/models";
import { createFragment } from "core/services/site/fragment";
import UploadManager from "core/services/uploadManager";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, FormElement, Icon, Modal } from "ui/components";
import Checkbox from "ui/components/atoms/CheckBox";
import { useEditorContext } from "ui/context/EditorContext";
import { useNotifications } from "ui/context/NotificationsContext";
import { usePublishContext } from "ui/context/PublishContext";
import { useSessionContext } from "ui/context/SessionContext";
import { useTaskContext } from "ui/context/TaskContext";
import { useFormValidation } from "ui/hooks/use-form-validation";
import useKijk from "ui/hooks/use-kijk";

import styles from "./SaveFragmentModal.module.scss";

interface Props {
    onClose(): void;
    channel: Channel;
}

const SaveFragmentModal: React.FC<Props> = ({ onClose, channel }) => {
    const navigate = useNavigate();
    const [pending, setPending] = React.useState<boolean>(false);
    const editorContext = useEditorContext();
    const sessionContext = useSessionContext();
    const taskContext = useTaskContext();
    const publishContext = usePublishContext();
    const [showTalpaKijk, setTalpaKijk] = React.useState<string | undefined>();
    const { addNotification } = useNotifications();
    const [clipDescription, setClipDescription] = React.useState<string>();

    const plan = sessionContext.selectedProject?.package ?? "free";

    const {
        handleSelectGenre,
        handleSelectEpisode,
        handleChangeOnlySeries,
        handleChangeExclusive,
        searchEpisodes,
        exportProperties,
        handleDescription,
        genres,
        episodes,
        selectedGenre,
        selectedEpisode,
        exclusive,
        onlySeries,
        episodesPending,
    } = useKijk(showTalpaKijk);

    React.useEffect(() => {
        const kijkIntegration = publishContext.accounts.find(
            (account) => account.publisherkey === "custom_talpakijkv2",
        );
        if (kijkIntegration) {
            setTalpaKijk(kijkIntegration.key);
        }
    }, [publishContext.accounts]);

    const theForm = useFormValidation({
        title: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 2,
            },
        },
        tags: {
            valid: true,
            touched: false,
            value: [],
            errorMsg: "",
            rules: {},
        },
        createanother: {
            valid: true,
            touched: false,
            value: "false",
            errorMsg: "",
            rules: {},
        },
        description: {
            valid: !publishContext.accounts.find(
                (account) => account.publisherkey === "custom_talpakijkv2",
            ),
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 2,
                maxLength: 750,
            },
        },
    });

    const saveFragment = async () => {
        setPending(true);
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("missing selected project");
            }
            const selectionsRequest = editorContext.selections
                .filter((selection) => selection.segments.length !== 0)
                .map((selection) => ({
                    trimStart: selection.trimStart,
                    trimEnd: selection.trimEnd,
                    firstSegmentNumber:
                        selection.segments[selection.segments.length - 1]
                            .number,
                    lastSegmentNumber: selection.segments[0].number,
                    segmentCount: selection.segments.length,
                }));
            // If we set a custom thumbnail we need to upload it seperately
            // and attach the file ID
            let thumbnailID;
            if (editorContext.thumbnailURL) {
                const blob = await fetch(editorContext.thumbnailURL).then((x) =>
                    x.blob(),
                );
                const file = new File([blob], "thumb.png");
                const uploadManager = new UploadManager(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                );
                const thumbnail = await uploadManager.uploadThumbnail(
                    file,
                    () => {}, // We can keep track of the upload progress here val*100
                );
                thumbnailID = thumbnail.value?.id;
            }
            const tagsValue = Array.isArray(theForm.formState.tags.value)
                ? theForm.formState.tags.value
                : [];
            const kijkProperties = showTalpaKijk
                ? exportProperties()
                : undefined;
            const response = await createFragment(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                channel.id,
                theForm.formState.title.value?.toString() || "",
                clipDescription,
                selectionsRequest,
                thumbnailID,
                [...tagsValue],
                kijkProperties,
            );
            if (response.error || !response.value) {
                throw new Error("CreateFragment call failed");
            }
            if (theForm.formState.createanother.value === "true") {
                const selections = [];
                const newSelection = new Selection();
                selections.push(newSelection);
                editorContext.setSelections(selections);
                editorContext.setCurrentSelectionID(newSelection.id);
                const createFragmentNotification = addNotification(
                    "normal",
                    "Creating Fragment",
                    `${theForm.formState.title.value} is being created`,
                    0,
                );
                taskContext.newTask(
                    response.value.processingTaskId,
                    createFragmentNotification.id,
                    "New Fragment successfully created",
                );
                onClose();
            } else {
                taskContext.update();
                const createFragmentNotification = addNotification(
                    "normal",
                    "Creating Fragment",
                    `${theForm.formState.title.value} is being created`,
                    0,
                );
                taskContext.newTask(
                    response.value.processingTaskId,
                    createFragmentNotification.id,
                    "New Fragment successfully created",
                );
                navigate("/clips");
            }
            setPending(false);
        } catch (err) {
            addNotification(
                "error",
                "Error during save fragment",
                (err as Error).message,
                5000,
            );
        }
    };
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!theForm.formValid) {
            return;
        }
        saveFragment();
    };

    const kijkJSX = (
        <div className={styles.kijk}>
            <div className={styles.kijk__heading}>
                <Icon name="kijk" /> <span>Kijk Metadata</span>
            </div>
            <div className={styles.formelement}>
                <Checkbox
                    title="Only search for series"
                    theme="dark"
                    id="onlySeries"
                    value={onlySeries.toString()}
                    className={styles.kijk__checkbox}
                    onChange={handleChangeOnlySeries}
                />
                <Checkbox
                    title="Exclusive"
                    theme="dark"
                    id="exclusive"
                    value={exclusive.toString()}
                    className={styles.kijk__checkbox}
                    onChange={handleChangeExclusive}
                />
            </div>
            <div className={styles.formelement}>
                <div className={styles.formelement__caption}>
                    Episode or series
                </div>
                <Dropdown
                    searchable
                    fullWidth
                    theme="dark"
                    value={selectedEpisode || ""}
                    options={episodes}
                    pending={episodesPending}
                    placeholderText="Select episode or series"
                    onValueChanged={handleSelectEpisode}
                    onSearch={searchEpisodes}
                />
            </div>
            <div className={styles.formelement}>
                <div className={styles.formelement__caption}>Genre</div>
                <Dropdown
                    theme="dark"
                    value={selectedGenre || ""}
                    placeholderText="Select genre"
                    options={genres}
                    onValueChanged={handleSelectGenre}
                />
            </div>
            <div className={styles.formelement__description}>
                <FormElement
                    required
                    defaultText="Add a description for this clip"
                    fieldState={theForm.formState.description}
                    theme="dark"
                    type="textarea"
                    id="description"
                    title="Description"
                    className={styles.kijk__description}
                    onChange={(value) => {
                        theForm.inputChangeHandler("description", value);
                        setClipDescription(value?.toString() || "");
                        handleDescription(value?.toString() || "");
                    }}
                />
            </div>
        </div>
    );

    const modalContent = (
        <form action="#" onSubmit={handleSubmit}>
            <button type="submit" className={styles.hidden} />
            {plan === "free" && (
                <span className={styles.freespoton}>
                    <Icon name="info" />
                    <span>
                        You are currently on our Free plan. This means we&#39;ll
                        burn in our SpotOn watermark in to your clip during
                        creation. Due to this, creating your clip may take
                        longer than expected.
                        <br />
                        <Link to="/settings/Plan">Upgrade your plan</Link> to
                        remove the watermark and create clips with lightning
                        speed.
                    </span>
                </span>
            )}
            <FormElement
                required
                theme="dark"
                defaultText="Name your clip"
                type="text"
                title="Title"
                id="title"
                fieldState={theForm.formState.title}
                onChange={(value) => {
                    theForm.inputChangeHandler("title", value);
                }}
            />
            <FormElement
                theme="dark"
                defaultText="Type a tag and press ',' or enter to add"
                type="tags"
                title="Tags"
                id="tags"
                fieldState={theForm.formState.tags}
                onChange={(value) => {
                    theForm.inputChangeHandler("tags", value);
                }}
            />

            {showTalpaKijk && kijkJSX}
        </form>
    );

    return (
        <Modal
            isForm
            content={modalContent}
            lineColor="amber"
            pending={pending ? "Creating clip…" : undefined}
            theme="dark"
            saveButtonText="Create"
            saveButtonDisabled={!theForm.formValid}
            title="Create"
            icon="save"
            extraButton={
                <FormElement
                    theme="dark"
                    type="checkbox"
                    fieldState={theForm.formState.createanother}
                    id="createanother"
                    title="Create another"
                    checkBoxPos="right"
                    onChange={(value) =>
                        theForm.inputChangeHandler("createanother", value)
                    }
                />
            }
            onClose={onClose}
            onSave={saveFragment}
        />
    );
};

export default SaveFragmentModal;
